import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  Loader2,
  Minus,
  Plus,
  Search,
  Sparkles,
  UserPlus,
  X,
} from "lucide-react";
import {
  closeAndGenerateTournament,
  declineTournamentPlayer,
  fetchTournamentClubPlayers,
  fetchTournamentTables,
  markTournamentPaid,
  openClubTournamentLiveRoom,
  publishTournament,
  readTournamentScorePhoto,
  reportTournamentScore,
  saveTournamentSettings,
  searchTournamentPlayers,
} from "../../components/funtionsBD/competitionService";
import {
  CATEGORY_PRESETS,
  buildTournamentSchedule,
  defaultTournamentName,
} from "../../utils/tournamentSchedule";
import TournamentScheduleView from "./TournamentScheduleView";
import "./TournamentSetupComposer.css";

let seq = 0;
function localId(prefix) {
  seq += 1;
  return `${prefix}-${Date.now()}-${seq}`;
}

export default function TournamentSetupComposer({
  open,
  mode = "dark",
  competitionId = null,
  initialView = null,
  onClose,
  onCreated,
}) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const currentUserId = useMemo(() => {
    try {
      return JSON.parse(localStorage.getItem("user") || "null")?.user_id || null;
    } catch {
      return null;
    }
  }, []);
  const [name, setName] = useState(defaultTournamentName());
  const [categories, setCategories] = useState([
    { id: localId("cat"), name: "Open" },
  ]);
  const [customCat, setCustomCat] = useState("");
  const [players, setPlayers] = useState([]);
  const [poolSize, setPoolSize] = useState(4);
  const [tableCount, setTableCount] = useState(4);
  const [tables, setTables] = useState([]);
  const [finalsMode, setFinalsMode] = useState("auto");
  const [qualifyCount, setQualifyCount] = useState(2);
  const [formatMode, setFormatMode] = useState("groups_knockout");
  const [visibility, setVisibility] = useState("members");
  const [feeEuros, setFeeEuros] = useState("0");
  const [capacity, setCapacity] = useState("");
  const [bestOf, setBestOf] = useState(5);
  const [swissRounds, setSwissRounds] = useState(4);
  const [eventDate, setEventDate] = useState("");
  const [startTime, setStartTime] = useState("");
  const [regOpen, setRegOpen] = useState("");
  const [regClose, setRegClose] = useState("");
  const [liveId, setLiveId] = useState(competitionId);
  const [liveView, setLiveView] = useState(initialView);
  const [clubPlayers, setClubPlayers] = useState([]);
  const [query, setQuery] = useState("");
  const [hits, setHits] = useState([]);
  const [searching, setSearching] = useState(false);
  const [guestName, setGuestName] = useState("");
  const [guestClub, setGuestClub] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [previewOpen, setPreviewOpen] = useState(false);

  const resetFromView = useCallback((view) => {
    if (!view) return;
    setName(view.competition?.name || defaultTournamentName());
    const cats = (view.categories || []).map((c) => ({
      id: c.category_id,
      name: c.name,
    }));
    setCategories(cats.length ? cats : [{ id: localId("cat"), name: "Open" }]);
    setPlayers(
      (view.players || []).map((p) => ({
        id: p.tournament_player_id,
        ...p,
      }))
    );
    if (view.config?.pool_size) setPoolSize(view.config.pool_size);
    if (view.config?.qualify_count) setQualifyCount(view.config.qualify_count);
    if (view.config?.table_count) setTableCount(view.config.table_count);
    if (view.config?.finals_mode) setFinalsMode(view.config.finals_mode);
    if (view.config?.format_mode) setFormatMode(view.config.format_mode);
    if (view.config?.visibility) setVisibility(view.config.visibility);
    if (view.config?.entry_fee_cents != null) {
      setFeeEuros(String(Number(view.config.entry_fee_cents) / 100));
    }
    if (view.config?.capacity != null) setCapacity(String(view.config.capacity));
    if (view.config?.games_to_win === 2) setBestOf(3);
    if (view.config?.games_to_win === 4) setBestOf(7);
    if (view.config?.games_to_win === 3) setBestOf(5);
    if (view.config?.swiss_rounds) setSwissRounds(view.config.swiss_rounds);
    if (view.config?.event_date) setEventDate(view.config.event_date);
    if (view.config?.start_time) setStartTime(view.config.start_time);
    setLiveView(view);
    if (view.competition?.competition_id) setLiveId(view.competition.competition_id);
  }, []);

  useEffect(() => {
    if (!open) return undefined;
    setError("");
    if (initialView) resetFromView(initialView);
    else {
      setName(defaultTournamentName());
      setCategories([{ id: localId("cat"), name: "Open" }]);
      setPlayers([]);
    }
    let cancelled = false;
    Promise.all([
      fetchTournamentClubPlayers().catch(() => []),
      fetchTournamentTables().catch(() => []),
    ]).then(([club, tbls]) => {
      if (cancelled) return;
      setClubPlayers(club || []);
      setTables(tbls || []);
      if (!initialView?.config?.table_count && tbls?.length) {
        setTableCount(tbls.length);
      }
    });
    return () => {
      cancelled = true;
    };
  }, [open, initialView, resetFromView]);

  useEffect(() => {
    if (!open) return undefined;
    const q = query.trim();
    if (q.length < 2) {
      setHits([]);
      return undefined;
    }
    const tmr = setTimeout(async () => {
      setSearching(true);
      try {
        const rows = await searchTournamentPlayers(q);
        setHits(rows || []);
      } catch {
        setHits([]);
      } finally {
        setSearching(false);
      }
    }, 280);
    return () => clearTimeout(tmr);
  }, [query, open]);

  const preview = useMemo(() => {
    if (formatMode !== "groups_knockout") {
      const count = players.filter((p) => p.presence !== "declined" && p.registration_status !== "declined").length;
      const label =
        formatMode === "single_elim"
          ? t("tourFormatElimPreview", "{{n}} players · single elimination. The bracket is drawn when registration closes.", { n: count })
          : t("tourFormatSwissPreview", "{{n}} players · Swiss. Round 1 is drawn when registration closes.", { n: count });
      return { headline: label, warnings: [], matches: [], categories: [] };
    }
    return buildTournamentSchedule({
      categories: categories.map((c) => ({
        id: c.id,
        name: c.name,
        pool_size: poolSize,
        finals_mode: finalsMode,
      })),
      players: players
        .filter((p) => p.presence !== "declined")
        .map((p) => ({
          id: p.id,
          name: p.name,
          club_name: p.club_name,
          ranking_points: p.ranking_points,
          category_ids: p.category_ids?.length
            ? p.category_ids
            : [categories[0]?.id].filter(Boolean),
        })),
      tables,
      table_count: tableCount,
      pool_size: poolSize,
      finals_mode: finalsMode,
    });
  }, [categories, players, poolSize, tableCount, tables, finalsMode, formatMode, t]);

  const settingsBody = () => ({
    name: name.trim() || defaultTournamentName(),
    kind: "tournament",
    pool_size: poolSize,
    qualify_count: qualifyCount,
    table_count: tableCount,
    tables: tables.slice(0, tableCount),
    finals_mode: finalsMode,
    format_mode: formatMode,
    visibility,
    entry_fee_euros: feeEuros,
    capacity: capacity === "" ? null : Number(capacity),
    best_of: bestOf,
    swiss_rounds: formatMode === "swiss" ? swissRounds : null,
    event_date: eventDate || null,
    start_time: startTime || null,
    registration_opens_at: regOpen || null,
    registration_closes_at: regClose || null,
    categories: categories.map((c) => ({
      id: c.id,
      category_id: c.category_id || c.id,
      name: c.name,
      pool_size: poolSize,
      qualify_count: qualifyCount,
      finals_mode: finalsMode,
      format_mode: formatMode,
    })),
    players: players.map((p, index) => ({
      ...p,
      tournament_player_id: p.tournament_player_id || (String(p.id).startsWith("tply") ? p.id : undefined),
      seed_order: index + 1,
      category_ids: p.category_ids?.length
        ? p.category_ids
        : [categories[0]?.id].filter(Boolean),
    })),
  });

  const applyView = (view) => {
    if (!view) return;
    resetFromView(view);
    onCreated?.(view);
  };

  const addCategory = (label) => {
    const name = String(label || "").trim();
    if (!name) return;
    if (categories.some((c) => c.name.toLowerCase() === name.toLowerCase())) return;
    setCategories((prev) => [...prev, { id: localId("cat"), name }]);
    setCustomCat("");
  };

  const removeCategory = (id) => {
    setCategories((prev) => prev.filter((c) => c.id !== id));
    setPlayers((prev) =>
      prev.map((p) => ({
        ...p,
        category_ids: (p.category_ids || []).filter((c) => c !== id),
      }))
    );
  };

  const upsertPlayer = (incoming) => {
    setPlayers((prev) => {
      const key = incoming.user_id
        ? (p) => p.user_id && String(p.user_id) === String(incoming.user_id)
        : (p) =>
            !p.user_id &&
            String(p.name).toLowerCase() === String(incoming.name).toLowerCase() &&
            String(p.club_name || "") === String(incoming.club_name || "");
      const existing = prev.find(key);
      if (existing) return prev;
      const defaultCats = categories.map((c) => c.id).slice(0, 1);
      return [
        ...prev,
        {
          id: incoming.tournament_player_id || incoming.id || localId("ply"),
          user_id: incoming.user_id || null,
          player_id: incoming.player_id || null,
          name: incoming.name,
          club_id: incoming.club_id || null,
          club_name: incoming.club_name || null,
          ranking_points: incoming.ranking_points ?? null,
          classement: incoming.classement || null,
          category_ids: incoming.category_ids?.length
            ? incoming.category_ids
            : defaultCats,
          presence: incoming.presence || "added",
          source: incoming.source || "organizer",
          is_host_club: Boolean(incoming.is_host_club),
        },
      ];
    });
  };

  const addGuest = () => {
    const n = guestName.trim();
    if (!n) return;
    upsertPlayer({
      name: n,
      guest_name: n,
      club_name: guestClub.trim() || t("tourVisiting", "Visiting"),
      source: "organizer",
    });
    setGuestName("");
    setGuestClub("");
  };

  const togglePlayerCat = (playerId, catId) => {
    setPlayers((prev) =>
      prev.map((p) => {
        if (p.id !== playerId) return p;
        const has = (p.category_ids || []).includes(catId);
        const next = has
          ? p.category_ids.filter((c) => c !== catId)
          : [...(p.category_ids || []), catId];
        return { ...p, category_ids: next.length ? next : [catId] };
      })
    );
  };

  const openRegistration = async () => {
    setBusy(true);
    setError("");
    try {
      const saved = await saveTournamentSettings(settingsBody(), liveId);
      const id = saved?.competition?.competition_id;
      const published = id ? await publishTournament(id) : saved;
      applyView(published);
    } catch (err) {
      setError(err?.response?.data?.message || err.message);
    } finally {
      setBusy(false);
    }
  };

  const generate = async () => {
    setBusy(true);
    setError("");
    try {
      const saved = await saveTournamentSettings(settingsBody(), liveId);
      const id = saved?.competition?.competition_id;
      const drawn = id ? await closeAndGenerateTournament(id) : saved;
      applyView(drawn);
    } catch (err) {
      setError(err?.response?.data?.message || err.message);
    } finally {
      setBusy(false);
    }
  };

  const phase = liveView?.config?.phase || "draft";
  const locked = phase === "live" || phase === "completed";

  if (!open) return null;

  return (
    <div className={`tour-composer tour-composer--${mode}`} role="dialog" aria-modal="true">
      <header className="tour-composer__bar">
        <div>
          <p className="tour-composer__kicker">
            {t("tourSetupKicker", "Set up in one screen")}
          </p>
          <h2>{t("tourSetupTitle", "Create tournament")}</h2>
        </div>
        <button type="button" onClick={onClose} aria-label={t("close", "Close")}>
          <X size={18} />
        </button>
      </header>

      <div className="tour-composer__grid">
        <div className="tour-composer__setup">
          <label className="tour-field">
            {t("competitionsTournamentName", "Name")}
            <input value={name} onChange={(e) => setName(e.target.value)} disabled={locked} />
          </label>

          <div className="tour-inline">
            <label className="tour-field">
              {t("tourEventDate", "Date")}
              <input type="date" value={eventDate} onChange={(e) => setEventDate(e.target.value)} disabled={locked} />
            </label>
            <label className="tour-field">
              {t("tourStartTime", "Start")}
              <input type="time" value={startTime} onChange={(e) => setStartTime(e.target.value)} disabled={locked} />
            </label>
          </div>

          <section>
            <h3>{t("tourFormat", "Format")}</h3>
            <div className="tour-chips">
              {[
                ["groups_knockout", t("tourFormatGroups", "Groups + knockout")],
                ["single_elim", t("tourFormatElim", "Single elimination")],
                ["swiss", t("tourFormatSwiss", "Swiss")],
              ].map(([id, label]) => (
                <button
                  key={id}
                  type="button"
                  className={formatMode === id ? "is-on" : ""}
                  onClick={() => setFormatMode(id)}
                  disabled={locked}
                >
                  {label}
                </button>
              ))}
            </div>
          </section>

          <section>
            <h3>{t("tourWhoCanEnter", "Who can enter")}</h3>
            <div className="tour-chips">
              {[
                ["members", t("tourVisMembers", "Club members")],
                ["link", t("tourVisLink", "Private link")],
                ["public", t("tourVisPublic", "Public page")],
              ].map(([id, label]) => (
                <button
                  key={id}
                  type="button"
                  className={visibility === id ? "is-on" : ""}
                  onClick={() => setVisibility(id)}
                  disabled={locked}
                >
                  {label}
                </button>
              ))}
            </div>
            {liveView?.public_path ? (
              <p className="tour-hint">
                <a href={liveView.public_path}>{liveView.public_path}</a>
              </p>
            ) : null}
          </section>

          <section className="tour-nums">
            <label className="tour-field">
              {t("tourFee", "Entry fee (€)")}
              <input
                inputMode="decimal"
                value={feeEuros}
                onChange={(e) => setFeeEuros(e.target.value)}
                disabled={locked}
              />
            </label>
            <label className="tour-field">
              {t("tourCapacity", "Places")}
              <input
                inputMode="numeric"
                value={capacity}
                placeholder={t("tourCapacityAny", "No limit")}
                onChange={(e) => setCapacity(e.target.value)}
                disabled={locked}
              />
            </label>
          </section>

          <section>
            <h3>{t("tourBestOf", "Games")}</h3>
            <div className="tour-chips">
              {[3, 5, 7].map((n) => (
                <button
                  key={n}
                  type="button"
                  className={bestOf === n ? "is-on" : ""}
                  onClick={() => setBestOf(n)}
                  disabled={locked}
                >
                  {t("tourBestOfN", "Best of {{n}}", { n })}
                </button>
              ))}
            </div>
            {formatMode === "swiss" ? (
              <label className="tour-field">
                {t("tourSwissRounds", "Swiss rounds")}
                <input
                  inputMode="numeric"
                  value={swissRounds}
                  onChange={(e) => setSwissRounds(Number(e.target.value) || 4)}
                  disabled={locked}
                />
              </label>
            ) : null}
          </section>

          <div className="tour-inline">
            <label className="tour-field">
              {t("tourRegOpens", "Entries open")}
              <input type="datetime-local" value={regOpen} onChange={(e) => setRegOpen(e.target.value)} disabled={locked} />
            </label>
            <label className="tour-field">
              {t("tourRegCloses", "Entries close")}
              <input type="datetime-local" value={regClose} onChange={(e) => setRegClose(e.target.value)} disabled={locked} />
            </label>
          </div>

          <section>
            <h3>{t("tourCategories", "Categories")}</h3>
            <div className="tour-chips">
              {CATEGORY_PRESETS.map((label) => (
                <button
                  key={label}
                  type="button"
                  className={
                    categories.some((c) => c.name === label) ? "is-on" : ""
                  }
                  onClick={() => addCategory(label)}
                >
                  {label}
                </button>
              ))}
            </div>
            <div className="tour-inline">
              <input
                value={customCat}
                onChange={(e) => setCustomCat(e.target.value)}
                placeholder={t("tourAddCategory", "Add category")}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    e.preventDefault();
                    addCategory(customCat);
                  }
                }}
              />
              <button type="button" onClick={() => addCategory(customCat)}>
                <Plus size={16} />
              </button>
            </div>
            <ul className="tour-cat-list">
              {categories.map((c) => (
                <li key={c.id}>
                  <span>{c.name}</span>
                  {categories.length > 1 ? (
                    <button type="button" onClick={() => removeCategory(c.id)}>
                      <X size={14} />
                    </button>
                  ) : null}
                </li>
              ))}
            </ul>
          </section>

          <section>
            <h3>{t("tourPlayers", "Players")}</h3>
            <p className="tour-hint">
              {t(
                "tourPlayersHint",
                "Host club members, players from other clubs, or walk-in guests. Tick the categories each person plays."
              )}
            </p>
            <div className="tour-search">
              <Search size={16} aria-hidden />
              <input
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder={t(
                  "tourSearchPlayers",
                  "Search any club by name…"
                )}
              />
              {searching ? <Loader2 size={14} className="tour-spin" /> : null}
            </div>
            {hits.length ? (
              <ul className="tour-hits">
                {hits.map((h) => (
                  <li key={h.user_id}>
                    <button type="button" onClick={() => upsertPlayer(h)}>
                      <strong>{h.name}</strong>
                      <em>
                        {h.club_name || t("tourUnknownClub", "Other club")}
                        {h.classement ? ` · ${h.classement}` : ""}
                      </em>
                    </button>
                  </li>
                ))}
              </ul>
            ) : null}

            <details className="tour-clublist">
              <summary>
                {t("tourHostClub", "Add from this club")} ({clubPlayers.length})
              </summary>
              <ul>
                {clubPlayers.map((h) => (
                  <li key={h.user_id}>
                    <button type="button" onClick={() => upsertPlayer(h)}>
                      {h.name}
                      {h.classement ? ` · ${h.classement}` : ""}
                    </button>
                  </li>
                ))}
              </ul>
            </details>

            <div className="tour-guest">
              <UserPlus size={16} aria-hidden />
              <input
                value={guestName}
                onChange={(e) => setGuestName(e.target.value)}
                placeholder={t("tourGuestName", "Guest name")}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    e.preventDefault();
                    addGuest();
                  }
                }}
              />
              <input
                value={guestClub}
                onChange={(e) => setGuestClub(e.target.value)}
                placeholder={t("tourGuestClub", "Club")}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    e.preventDefault();
                    addGuest();
                  }
                }}
              />
              <button type="button" onClick={addGuest}>
                {t("tourAddGuest", "Add")}
              </button>
            </div>

            <ul className="tour-people">
              {players.map((p) => (
                <li key={p.id}>
                  <span className="tour-ava">
                    {String(p.name || "?")
                      .split(/\s+/)
                      .slice(0, 2)
                      .map((x) => x[0])
                      .join("")
                      .toUpperCase()}
                  </span>
                  <span className="tour-people__who">
                    <strong>{p.name}</strong>
                    <em>
                      {p.club_name || t("tourUnknownClub", "Other club")}
                      {p.registration_status && p.registration_status !== "confirmed"
                        ? ` · ${String(p.registration_status).replace(/_/g, " ")}`
                        : ""}
                    </em>
                    <span className="tour-people__cats">
                      {categories.map((c) => (
                        <button
                          key={c.id}
                          type="button"
                          className={(p.category_ids || []).includes(c.id) ? "is-on" : ""}
                          onClick={() => togglePlayerCat(p.id, c.id)}
                        >
                          {c.name}
                        </button>
                      ))}
                    </span>
                  </span>
                  {p.registration_status === "pay_at_club" && p.tournament_player_id && liveId ? (
                    <button
                      type="button"
                      onClick={async () => {
                        setBusy(true);
                        try {
                          applyView(await markTournamentPaid(liveId, p.tournament_player_id));
                        } catch (err) {
                          setError(err?.response?.data?.message || err.message);
                        } finally {
                          setBusy(false);
                        }
                      }}
                    >
                      {t("tourMarkPaid", "Mark paid")}
                    </button>
                  ) : null}
                  <button
                    type="button"
                    className="tour-people__rm"
                    onClick={async () => {
                      if (p.tournament_player_id && liveId && p.source !== "organizer") {
                        setBusy(true);
                        try {
                          applyView(await declineTournamentPlayer(liveId, p.tournament_player_id));
                        } catch (err) {
                          setError(err?.response?.data?.message || err.message);
                        } finally {
                          setBusy(false);
                        }
                        return;
                      }
                      setPlayers((prev) => prev.filter((x) => x.id !== p.id));
                    }}
                    aria-label={t("remove", "Remove")}
                  >
                    <X size={14} />
                  </button>
                </li>
              ))}
            </ul>
          </section>

          <section className="tour-nums">
            {formatMode === "groups_knockout" ? (
              <div>
                <h3>{t("tourPoolSize", "Players per pool")}</h3>
                <div className="tour-chips">
                  {[3, 4, 5].map((n) => (
                    <button
                      key={n}
                      type="button"
                      className={poolSize === n ? "is-on" : ""}
                      onClick={() => setPoolSize(n)}
                    >
                      {n}
                    </button>
                  ))}
                </div>
              </div>
            ) : null}
            <div>
              <h3>{t("tourTables", "Tables")}</h3>
              <div className="tour-stepper">
                <button
                  type="button"
                  onClick={() => setTableCount((n) => Math.max(1, n - 1))}
                >
                  <Minus size={14} />
                </button>
                <strong>{tableCount}</strong>
                <button
                  type="button"
                  onClick={() => setTableCount((n) => Math.min(40, n + 1))}
                >
                  <Plus size={14} />
                </button>
              </div>
            </div>
            {formatMode === "groups_knockout" ? (
              <div>
                <h3>{t("tourQualify", "Advance")}</h3>
                <div className="tour-chips">
                  {[1, 2, 3].map((n) => (
                    <button
                      key={n}
                      type="button"
                      className={qualifyCount === n ? "is-on" : ""}
                      onClick={() => setQualifyCount(n)}
                      disabled={locked}
                    >
                      {n}
                    </button>
                  ))}
                </div>
              </div>
            ) : null}
          </section>

          {formatMode === "groups_knockout" ? (
          <section>
            <h3>{t("tourFinals", "Finals")}</h3>
            <p className="tour-hint">
              {preview.categories
                .map((c) =>
                  c.finals_mode === "knockout"
                    ? t(
                        "tourFinalsKnockoutAuto",
                        "{{name}}: knockout after pools",
                        { name: c.name }
                      )
                    : t(
                        "tourFinalsTableAuto",
                        "{{name}}: final table after pools",
                        { name: c.name }
                      )
                )
                .join(" · ")}
            </p>
            <div className="tour-chips">
              {["auto", "final_table", "knockout"].map((m) => (
                <button
                  key={m}
                  type="button"
                  className={finalsMode === m ? "is-on" : ""}
                  onClick={() => setFinalsMode(m)}
                >
                  {m === "auto"
                    ? t("tourFinalsAuto", "Auto")
                    : m === "final_table"
                      ? t("tourFinalTable", "Final table")
                      : t("tourKnockout", "Knockout")}
                </button>
              ))}
            </div>
          </section>
          ) : null}

          {error ? <p className="tour-error">{error}</p> : null}
        </div>

        <aside className={`tour-composer__preview ${previewOpen ? "is-open" : ""}`}>
          <TournamentScheduleView
            view={phase === "drawn" || phase === "live" || phase === "completed" ? liveView : null}
            preview={preview}
            categories={categories}
            players={players}
            currentUserId={currentUserId}
            canScore={phase === "drawn" || phase === "live"}
            canOpenLive={phase === "drawn" || phase === "live"}
            gamesToWin={bestOf === 3 ? 2 : bestOf === 7 ? 4 : 3}
            busy={busy}
            onScore={async (matchId, sets) => {
              if (!liveId) return;
              setBusy(true);
              setError("");
              try {
                applyView(await reportTournamentScore(liveId, matchId, sets));
              } catch (err) {
                setError(err?.response?.data?.message || err.message);
              } finally {
                setBusy(false);
              }
            }}
            onOpenLive={async (matchId) => {
              if (!liveId) return;
              setBusy(true);
              setError("");
              try {
                const room = await openClubTournamentLiveRoom(liveId, matchId);
                if (room?.championship_match_id) {
                  navigate(`/competition/match/${room.championship_match_id}`);
                }
              } catch (err) {
                setError(err?.response?.data?.message || err.message);
              } finally {
                setBusy(false);
              }
            }}
            onScorePhoto={async (matchId, imageJpegBase64) => {
              if (!liveId) return null;
              return readTournamentScorePhoto(liveId, matchId, imageJpegBase64);
            }}
          />
        </aside>
      </div>

      <footer className="tour-composer__cta">
        <button
          type="button"
          className="tour-composer__ghost"
          onClick={() => setPreviewOpen((v) => !v)}
        >
          {t("tourSeeSchedule", "See schedule")}
        </button>
        {phase === "draft" || phase === "open" ? (
          <button type="button" className="tour-composer__secondary" disabled={busy || locked} onClick={openRegistration}>
            {t("tourOpenReg", "Open registration")}
          </button>
        ) : null}
        <button
          type="button"
          className="tour-composer__go"
          disabled={busy || locked}
          onClick={generate}
        >
          {busy ? <Loader2 size={16} className="tour-spin" /> : <Sparkles size={16} />}
          {t("tourCloseAndDraw", "Close registration and draw")}
        </button>
      </footer>
    </div>
  );
}
